import { useState, type ReactNode } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  Delete02Icon,
  FloppyDiskIcon,
  PencilEdit02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { cx } from "shared/utils";
import type { LeadSavedView } from "../../types";
import type { LeadSavedViewsController } from "./useLeadSavedViewsController";

type SavedViewsControlProps = LeadSavedViewsController["controlProps"];

const DEFAULT_VIEW_LABEL = "Default";
const HELPER_TEXT =
  "You can create a custom view by editing the filters and saving the view";

const ViewRow = ({
  label,
  isActive,
  onApply,
  actions
}: {
  label: string;
  isActive: boolean;
  onApply: () => void;
  actions?: ReactNode;
}) => (
  <div className="group flex items-center gap-1 rounded-lg pr-1 hover:bg-alpha-40">
    <button
      type="button"
      onClick={onApply}
      className="flex min-w-0 flex-1 items-center px-3 py-2 text-left"
    >
      <span
        className={cx(
          "truncate text-sm",
          isActive
            ? "font-semibold text-content-1000"
            : "font-medium text-content-700"
        )}
      >
        {label}
      </span>
    </button>
    <Show if={Boolean(actions)}>
      <div className="flex shrink-0 items-center gap-0.5">{actions}</div>
    </Show>
  </div>
);

export const SavedViewsControl = ({
  views,
  isLoading,
  activeViewId,
  isModified,
  isOverwriting,
  onApply,
  onSaveCurrent,
  onRename,
  onOverwrite,
  onDelete
}: SavedViewsControlProps) => {
  const [open, setOpen] = useState(false);

  const activeView = views.find((view) => view.id === activeViewId) ?? null;
  const triggerLabel = isModified
    ? "Custom"
    : (activeView?.name ?? DEFAULT_VIEW_LABEL);

  const closeAnd = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  const renderActions = (view: LeadSavedView) => (
    <>
      {/* Overwrite only makes sense when there are unsaved changes to save
          into the view. */}
      <Show if={isModified}>
        <IconButton
          aria-label={`Overwrite ${view.name} with current filters`}
          HugeIcon={FloppyDiskIcon}
          size={IconButtonSize.XS}
          variant={IconButtonVariant.TEXT_BASE}
          disabled={isOverwriting}
          onClick={closeAnd(() => onOverwrite(view))}
        />
      </Show>
      <IconButton
        aria-label={`Rename ${view.name}`}
        HugeIcon={PencilEdit02Icon}
        size={IconButtonSize.XS}
        variant={IconButtonVariant.TEXT_BASE}
        onClick={closeAnd(() => onRename(view))}
      />
      <IconButton
        aria-label={`Delete ${view.name}`}
        HugeIcon={Delete02Icon}
        size={IconButtonSize.XS}
        variant={IconButtonVariant.TEXT_BASE}
        onClick={closeAnd(() => onDelete(view))}
      />
    </>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      placement="bottom-end"
      offsetValue={4}
    >
      <PopoverTrigger asChild onClick={() => setOpen(true)}>
        <button
          type="button"
          aria-label="Saved views"
          className="flex h-10 items-center gap-2 rounded-lg border border-alpha-100 bg-white px-3 shadow-xs"
        >
          <span className="text-xs font-bold text-content-200">View:</span>
          <span className="max-w-[140px] truncate text-xs font-bold text-content-1000">
            {triggerLabel}
          </span>
          {/* Status hint only — saving is done from the "Save current view"
              action inside the dropdown, not by clicking this label. */}
          <Show if={isModified}>
            <span className="text-xs font-bold text-primary-500">
              Save view
            </span>
          </Show>
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            size={16}
            className={cx(
              "shrink-0 text-content-1000",
              open ? "rotate-180" : ""
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="z-10 w-[280px] rounded-lg border border-alpha-100 bg-white p-1 shadow-lg">
        <div className="max-h-[320px] overflow-y-auto">
          <ViewRow
            label={DEFAULT_VIEW_LABEL}
            isActive={activeViewId === null && !isModified}
            onApply={closeAnd(() => onApply(null))}
          />
          {views.map((view) => (
            <ViewRow
              key={view.id}
              label={view.name}
              isActive={view.id === activeViewId && !isModified}
              onApply={closeAnd(() => onApply(view))}
              actions={renderActions(view)}
            />
          ))}
          <Show if={isLoading && views.length === 0}>
            <p className="px-3 py-2 text-sm font-medium text-content-400">
              Loading views…
            </p>
          </Show>
        </div>

        <Show if={isModified}>
          <div className="mt-1 border-t border-alpha-100 p-1">
            <button
              type="button"
              onClick={closeAnd(onSaveCurrent)}
              className="w-full rounded-lg border border-alpha-100 px-3 py-2 text-sm font-semibold text-content-1000 hover:bg-alpha-40"
            >
              Save current view
            </button>
          </div>
        </Show>
        <Show if={!isModified}>
          <div className="mt-1 border-t border-alpha-100 px-3 py-2">
            <p className="text-xs font-medium text-content-400">
              {HELPER_TEXT}
            </p>
          </div>
        </Show>
      </PopoverContent>
    </Popover>
  );
};
